import { apiFetch } from "@/lib/api";
import type { SearchableSelectOption } from "@/components/ui/SearchableSelect";

export interface DocumentKind {
  code: string;
  label: string;
  sort_order: number;
  active: boolean;
  /** Operator-facing hover hint; may be null for legacy rows until migrations run. */
  hint: string | null;
}

/**
 * Text for the hover hint with glyph next to document type.
 * `context` distinguishes the workbench filter (empty means all types) from upload or edit fields (empty means not set).
 */
export function documentKindHintForSelection(
  kinds: DocumentKind[],
  code: string,
  context: "filter" | "field",
): string {
  const trimmed = code.trim();
  if (trimmed === "") {
    return context === "filter"
      ? "No document type filter is applied; every type that matches the rest of your filters is shown."
      : "Pick a category so this revision lines up with others under the same title and applicability. For a single file upload, choose a type here. Bulk uploads can stay not set and you can set type per row after upload.";
  }
  if (trimmed === "__unset__") {
    return "Show only revisions whose document type is still not set (common right after bulk upload).";
  }
  const row = kinds.find((item) => item.code === trimmed);
  const fromDb = row?.hint?.trim();
  if (fromDb) {
    return fromDb;
  }
  if (row) {
    return `${row.label}: controlled document category used for filters and revision matching.`;
  }
  return "Unknown document type.";
}

/** Options for `SearchableSelect`: primary line is the label; `description` is operator copy from `document_kind.hint` or fallbacks (`documentKindHintForSelection`). Omitted on **Not set** so the list stays compact. */
export function documentKindWorkbenchFieldOptions(kinds: DocumentKind[]): SearchableSelectOption[] {
  return [
    {
      value: "",
      label: "Not set",
    },
    ...kinds.map((kind) => ({
      value: kind.code,
      label: kind.label,
      description: documentKindHintForSelection(kinds, kind.code, "field"),
    })),
  ];
}

/** Filter rail: includes synthetic `__unset__` and `All types` / `Not set` rows; **Not set** omits `description`, **All types** keeps it. */
export function documentKindWorkbenchFilterOptions(kinds: DocumentKind[]): SearchableSelectOption[] {
  return [
    {
      value: "",
      label: "All types",
      description: documentKindHintForSelection(kinds, "", "filter"),
    },
    {
      value: "__unset__",
      label: "Not set",
    },
    ...kinds.map((kind) => ({
      value: kind.code,
      label: kind.label,
      description: documentKindHintForSelection(kinds, kind.code, "filter"),
    })),
  ];
}

export interface DocumentSkuOption {
  sku: string;
  product_name: string | null;
  mv_description: string | null;
}

export type DocumentShareScopeType = "document" | "sku";

export interface DocumentShareGrant {
  id: string;
  org_id: number;
  grantee_user_id: string;
  scope_type: DocumentShareScopeType;
  document_record_id: string | null;
  sku: string | null;
  created_by: string | null;
  created_at: string;
  grantee_display_name: string | null;
  grantee_email: string | null;
  document_title: string | null;
}

export interface DocumentShareGranteeOption {
  userId: string;
  label: string;
  email: string | null;
}

export interface ProductCollection {
  id: string;
  org_id: number;
  code: string;
  name: string;
  description: string | null;
  notes: string | null;
  active: boolean;
  /** When set, every `public.sku` with this BoM template is a member unless excluded or listed explicitly. */
  bom_template_id: number | null;
  bom_template_name: string | null;
  created_at: string;
  updated_at: string;
}

export type ProductCollectionSkuMembershipSource = "explicit" | "template";

export interface ProductCollectionSku {
  product_collection_id: string;
  org_id: number;
  sku: string;
  product_name: string | null;
  mv_description: string | null;
  notes: string | null;
  created_at: string | null;
  membership_source: ProductCollectionSkuMembershipSource;
}

/** SKUs omitted from template-driven membership (explicit members are unchanged). */
export interface ProductCollectionTemplateSkuExclusion {
  sku: string;
  product_name: string | null;
  notes: string | null;
  created_at: string;
}

export interface DocumentCollectionSkusResponse {
  skus: ProductCollectionSku[];
  template_sku_exclusions: ProductCollectionTemplateSkuExclusion[];
}

export interface ProductCollectionRelation {
  id: string;
  org_id: number;
  from_collection_id: string;
  from_collection_code: string;
  from_collection_name: string;
  to_collection_id: string;
  to_collection_code: string;
  to_collection_name: string;
  relation_type: "part_of" | "related_to" | "replaces" | "sibling_of" | "used_with";
  notes: string | null;
  created_at: string;
}

export interface ProductTransitionNotice {
  id: string;
  org_id: number;
  product_collection_id: string;
  product_collection_code: string;
  product_collection_name: string;
  notice_number: string;
  title: string;
  status: "draft" | "issued" | "completed" | "cancelled";
  transition_type: "immediate_cutover" | "runout" | "date_based" | "order_based" | "lot_based" | "hybrid";
  from_sku: string | null;
  from_sku_product_name: string | null;
  to_sku: string | null;
  to_sku_product_name: string | null;
  effective_rule: string;
  effective_date: string | null;
  applies_to: string | null;
  reason: string | null;
  notes: string | null;
  created_by: string | null;
  updated_by: string | null;
  issued_by: string | null;
  issued_at: string | null;
  completed_by: string | null;
  completed_at: string | null;
  cancelled_by: string | null;
  cancelled_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ProductCollectionEvent {
  id: string;
  org_id: number;
  product_collection_id: string;
  transition_notice_id: string | null;
  event_type: string;
  event_note: string | null;
  from_sku: string | null;
  to_sku: string | null;
  created_by: string | null;
  created_by_email: string | null;
  created_at: string;
}

export type DrawingCoverageStatus = "missing" | "wip" | "approved";

export interface DrawingCoverageDocument {
  id: string;
  title: string;
  revision: string | null;
  original_filename: string | null;
  on_sku: boolean;
  collection_codes: string[];
}

export interface DeviceDrawingCoverageGap {
  sku: string;
  product_name: string | null;
  mv_description: string | null;
  drawing_2d_status: DrawingCoverageStatus;
  drawing_3d_status: DrawingCoverageStatus;
  drawing_2d: DrawingCoverageDocument | null;
  drawing_3d: DrawingCoverageDocument | null;
  has_2d_drawing: boolean;
  has_3d_drawing: boolean;
  has_2d_on_sku: boolean;
  has_3d_on_sku: boolean;
  collection_codes_2d: string[];
  collection_codes_3d: string[];
}

export interface DocumentChangeOrder {
  id: string;
  org_id: number;
  number: string;
  title: string;
  description: string | null;
  engineering_change_notice: string | null;
  change_reason: string | null;
  status: "draft" | "submitted" | "approved" | "implemented" | "cancelled";
  requested_by: string | null;
  approved_by: string | null;
  implemented_by: string | null;
  requested_at: string | null;
  approved_at: string | null;
  implemented_at: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  document_count: string;
}

export interface DocumentTarget {
  target_type: "sku" | "collection";
  sku: string | null;
  sku_product_name: string | null;
  mv_description: string | null;
  mv_variant: string | null;
  product_collection_id: string | null;
  product_collection_code: string | null;
  product_collection_name: string | null;
}

export interface ControlledDocument {
  id: string;
  org_id: number;
  storage_bucket: string;
  storage_object_path: string;
  original_filename: string;
  content_type: string | null;
  file_size_bytes: string;
  title: string;
  description: string | null;
  document_kind_code: string | null;
  document_kind_label: string;
  revision: string;
  lifecycle_status: "draft" | "wip" | "current" | "obsolete";
  sku: string | null;
  sku_product_name: string | null;
  sku_mv_description: string | null;
  sku_mv_variant: string | null;
  product_family_id: string | null;
  product_family_code: string | null;
  product_family_name: string | null;
  change_order_id: string | null;
  change_order_number: string | null;
  engineering_change_notice: string | null;
  change_notes: string | null;
  google_drive_file_id: string | null;
  google_drive_mirrored_at: string | null;
  google_drive_mirror_error: string | null;
  created_by: string | null;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
  created_by_email: string | null;
  owner_user_id?: string | null;
  owner_display_name?: string | null;
  owner_email?: string | null;
  owner_avatar_url?: string | null;
  targets: DocumentTarget[];
  can_preview?: boolean;
  can_download?: boolean;
  can_read?: boolean;
}

export interface SpreadsheetPreviewColumn {
  key: string;
  label: string;
}

export interface SpreadsheetPreviewSheetSummary {
  index: number;
  name: string;
}

export interface SpreadsheetPreviewSheet {
  index: number;
  name: string;
  header_row_index: number | null;
  columns: SpreadsheetPreviewColumn[];
  rows: string[][];
}

export interface SpreadsheetPreviewResponse {
  sheets: SpreadsheetPreviewSheetSummary[];
  active_sheet_index: number;
  sheet: SpreadsheetPreviewSheet;
  truncated_rows: boolean;
  truncated_columns: boolean;
}

export interface DocumentRevisionEvent {
  id: string;
  org_id: number;
  document_record_id: string;
  change_order_id: string | null;
  change_order_number: string | null;
  event_type: string;
  event_note: string | null;
  created_by: string | null;
  created_by_email: string | null;
  created_by_display_name: string | null;
  created_at: string;
}

export interface DocumentInboundEmailThread {
  subject: string | null;
  from_email: string | null;
  received_at: string | null;
  thread_text: string;
}

export interface DocumentRevisionChange {
  id: string;
  org_id: number;
  document_record_id: string;
  change_order_id: string | null;
  field_name: string | null;
  changed_from: string | null;
  changed_to: string | null;
  reason: string | null;
  created_by: string | null;
  created_at: string;
}

export interface DocumentRevisionChangeWithDocument extends DocumentRevisionChange {
  document_title: string;
}

export interface DocumentNoteTarget {
  target_type: "sku" | "collection" | "change_order";
  sku: string | null;
  sku_product_name: string | null;
  product_collection_id: string | null;
  product_collection_code: string | null;
  product_collection_name: string | null;
  change_order_id: string | null;
  change_order_number: string | null;
}

export interface DocumentNote {
  id: string;
  org_id: number;
  sku: string | null;
  product_family_id: string | null;
  product_collection_id: string | null;
  change_order_id: string | null;
  body: string;
  created_by: string | null;
  created_at: string;
  created_by_email: string | null;
  targets: DocumentNoteTarget[];
}

export function formatBytes(raw: string): string {
  const n = Number.parseInt(raw, 10);
  if (!Number.isFinite(n) || n < 0) return raw;
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatTimestamp(iso: string | null): string {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function formatEcoNumber(number: string): string {
  const trimmed = number.trim();
  return /^\d+$/.test(trimmed) ? `ECO-${trimmed}` : trimmed;
}

export function formatEcnNumber(number: string): string {
  const trimmed = number.trim();
  return /^\d+$/.test(trimmed) ? `ECN-${trimmed}` : trimmed;
}

/** Native-tooltip text for SKU pills: Megaventory description and variant joined by one space (both trimmed). */
export function skuMegaventoryHoverTitle(
  mvDescription: string | null | undefined,
  mvVariant: string | null | undefined,
): string | undefined {
  const description = mvDescription?.trim() ?? "";
  const variant = mvVariant?.trim() ?? "";
  if (description !== "" && variant !== "") return `${description} ${variant}`;
  if (description !== "") return description;
  if (variant !== "") return variant;
  return undefined;
}

/** SearchableSelect / combobox secondary line: `mv_description` (or product name) plus space plus `mv_variant`. */
export function formatSkuMegaventoryCatalogDescription(
  mvDescription: string | null | undefined,
  mvVariant: string | null | undefined,
): string {
  return skuMegaventoryHoverTitle(mvDescription, mvVariant) ?? "";
}

/** Labels structured changelog `field_name` values from the API for display. */
export function formatRevisionChangeFieldLabel(fieldName: string | null): string {
  if (fieldName == null || fieldName.trim() === "") return "Metadata";
  switch (fieldName) {
    case "title":
      return "Title";
    case "description":
      return "Description";
    case "document_kind_code":
      return "Document type";
    case "revision":
      return "Revision";
    case "change_notes":
      return "Revision change notes";
    default:
      return fieldName.replace(/_/g, " ");
  }
}

/** Lowercase file extension from the stored filename (no leading dot), or null when absent or malformed. */
export function filenameExtension(filename: string): string | null {
  const base = filename.replace(/^.*[/\\]/, "");
  const lastDot = base.lastIndexOf(".");
  if (lastDot <= 0 || lastDot >= base.length - 1) return null;
  const extension = base.slice(lastDot + 1).trim().toLowerCase();
  return extension.length > 0 ? extension : null;
}

export {
  documentListActiveRevisionLabel as controlledDocumentListActiveRevisionLabel,
  documentListRevisionSummary as controlledDocumentListRevisionSummary,
  documentListStatusSummary as controlledDocumentListStatusSummary,
  documentSharesRevisionLineage,
  documentTitleAndKindKey,
  documentTargetsOverlap,
  formatRevisionLetter,
  groupDocumentsForList as groupControlledDocumentsForList,
  nextRevisionLetter,
  parseRevisionLetter,
  type ControlledDocumentListGroup,
} from "@/lib/documentsRevisionLogic";

/** True when the user may download the file (or copy it onto an RFQ). Catalog rows can exist without this. */
export function controlledDocumentIsDownloadable(document: {
  can_download?: boolean;
  can_read?: boolean;
}): boolean {
  if (document.can_download != null) return document.can_download;
  return document.can_read === true;
}

/** True when the user may open the preview sidebar. Shares also grant preview. */
export function controlledDocumentIsPreviewable(document: {
  can_preview?: boolean;
  can_download?: boolean;
  can_read?: boolean;
}): boolean {
  if (document.can_preview != null) return document.can_preview;
  return controlledDocumentIsDownloadable(document);
}
export async function downloadControlledDocument(document: ControlledDocument): Promise<void> {
  const id = encodeURIComponent(document.id);
  const { signedUrl, originalFilename } = await apiFetch<{ signedUrl: string; originalFilename: string }>(
    `/api/documents/${id}/signed-url`,
    { cache: "no-store" },
  );
  const response = await fetch(signedUrl, { cache: "no-store", mode: "cors" });
  if (!response.ok) {
    throw new Error(`Download failed (${response.status}).`);
  }
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const baseName = originalFilename.replace(/^.*[/\\]/, "").trim() || "document";
  const anchor = window.document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = baseName;
  anchor.rel = "noopener";
  window.document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(objectUrl);
}

export function targetLabel(document: ControlledDocument): string {
  if (document.targets?.length > 0) {
    return document.targets
      .map((target) => {
        if (target.target_type === "sku" && target.sku) {
          return target.sku_product_name ? `${target.sku} - ${target.sku_product_name}` : target.sku;
        }
        if (target.target_type === "collection" && target.product_collection_name) {
          return `${target.product_collection_code ?? "Collection"} - ${target.product_collection_name}`;
        }
        return "Unassigned";
      })
      .join(", ");
  }
  if (document.sku) {
    return document.sku_product_name ? `${document.sku} - ${document.sku_product_name}` : document.sku;
  }
  if (document.product_family_name) {
    return `${document.product_family_code ?? "Collection"} - ${document.product_family_name}`;
  }
  return "Unassigned";
}

export interface DocumentCollectionsTreeResponse {
  collections: ProductCollection[];
  /** Each edge: child `part_of` parent (`from` → `to` in the database). */
  part_of_edges: Array<{ child_collection_id: string; parent_collection_id: string }>;
}

export async function fetchDocumentCollectionsTree(): Promise<DocumentCollectionsTreeResponse> {
  return apiFetch<DocumentCollectionsTreeResponse>("/api/document-collections/tree");
}

export async function fetchDocumentLookups(): Promise<{
  kinds: DocumentKind[];
  collections: ProductCollection[];
  changeOrders: DocumentChangeOrder[];
}> {
  const [kinds, collections, changeOrders] = await Promise.all([
    apiFetch<{ kinds: DocumentKind[] }>("/api/document-kinds"),
    apiFetch<{ collections: ProductCollection[] }>("/api/document-collections"),
    apiFetch<{ changeOrders: DocumentChangeOrder[] }>("/api/document-change-orders"),
  ]);
  return {
    kinds: kinds.kinds,
    collections: collections.collections,
    changeOrders: changeOrders.changeOrders,
  };
}

